import { useTranslation } from 'react-i18next'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import useUserCertificationAssignment from '../hooks/useUserCertificationAssignment'

export default function UserCertificationAssignment({
	certificates,
	disabled = false,
	onCertificatesChange,
}) {
	const { t } = useTranslation()
	const {
		assignedCertifications,
		availableCertifications,
		filteredAvailableCertifications,
		handleAddCertification,
		handleCloseAddCertificationModal,
		handleOpenAddCertificationModal,
		handleRemoveCertification,
		isAddCertificationModalOpen,
		loading,
		searchCertificationTerm,
		selectedCertification,
		setSearchCertificationTerm,
		setSelectedCertification,
	} = useUserCertificationAssignment({
		certificates,
		onCertificatesChange,
		disabled,
	})

	if (loading) {
		return <LoadingIndicator message={t('trainingTrainers.details.loading')} />
	}

	return (
		<div className="rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex items-center justify-between gap-3">
				<h2 className="text-sm font-semibold text-gray-900">{t('trainingTrainers.details.sections.certifications')}</h2>
				<button
					type="button"
					onClick={handleOpenAddCertificationModal}
					disabled={disabled || !availableCertifications.length}
					className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{t('trainingTrainers.details.actions.addCertification')}
				</button>
			</div>

			{!availableCertifications.length && (
				<p className="mt-2 text-sm text-gray-500">{t('trainingTrainers.details.messages.noAvailableCertifications')}</p>
			)}

			<div className="mt-4 space-y-3">
				{assignedCertifications.length
					? assignedCertifications.map(certification => (
						<div key={certification._id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
							<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
								<div className="min-w-0 flex-1">
									<p className="text-sm font-semibold text-gray-900">{certification.name || '—'}</p>
									<p className="mt-1 text-sm text-gray-500">{certification.description || '—'}</p>
								</div>
								<button
									type="button"
									onClick={() => handleRemoveCertification(certification)}
									disabled={disabled}
									className="inline-flex h-8 w-full items-center justify-center rounded-lg border border-red-100 bg-red-50 px-4 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 sm:w-[12rem]"
								>
									<span className="truncate">{t('trainingTrainers.details.actions.removeCertification')}</span>
								</button>
							</div>
						</div>
					))
					: <span className="text-sm text-gray-500">{t('trainingTrainers.details.emptyCertifications')}</span>}
			</div>

			{isAddCertificationModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
					<div className="absolute inset-0 bg-black/40" onClick={handleCloseAddCertificationModal} />
					<div className="relative w-full max-w-3xl rounded-lg bg-white shadow-xl">
						<div className="p-5">
							<h3 className="text-base font-semibold text-gray-900">
								{t('trainingTrainers.details.certificationModal.title')}
							</h3>
							<p className="mt-1 text-sm text-gray-500">
								{t('trainingTrainers.details.certificationModal.description')}
							</p>
						</div>

						<div className="border-t border-gray-200 px-5 py-4">
							<input
								type="text"
								value={searchCertificationTerm}
								onChange={event => setSearchCertificationTerm(event.target.value)}
								placeholder={t('trainingTrainers.details.certificationModal.searchPlaceholder')}
								autoFocus
								className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>

							<div className="mt-4 max-h-80 overflow-y-auto rounded-md border border-gray-200">
								{filteredAvailableCertifications.length ? filteredAvailableCertifications.map(certification => {
									const isSelected = String(selectedCertification?._id || '') === String(certification?._id || '')

									return (
										<button
											key={certification._id}
											type="button"
											onClick={() => setSelectedCertification(certification)}
											className={`block w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 ${
												isSelected ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'
											}`}
										>
											<p className="text-sm font-medium text-gray-900">{certification?.name || '—'}</p>
											<p className="mt-1 text-sm text-gray-500">{certification?.description || '—'}</p>
										</button>
									)
								}) : (
									<div className="px-4 py-6 text-sm text-gray-500">
										{t('trainingTrainers.details.certificationModal.emptyOptions')}
									</div>
								)}
							</div>
						</div>

						<div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
							<button
								type="button"
								onClick={() => setSelectedCertification(null)}
								className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
							>
								{t('trainingTrainers.details.certificationModal.clearSelection')}
							</button>

							<div className="flex items-center gap-3">
								<button
									type="button"
									onClick={handleCloseAddCertificationModal}
									className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
								>
									{t('notifications.cancelButton')}
								</button>
								<button
									type="button"
									onClick={handleAddCertification}
									disabled={disabled || !selectedCertification}
									className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{t('trainingTrainers.details.certificationModal.choose')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
